/**
 * ProjetoTiposPage — /admin/projeto-tipos (A4.3.6 / Master Plan A4).
 *
 * Admin CRUD de tipos de projeto + editor das etapas de aprovação configuráveis
 * (Bloco 5.3=C: fluxo customizável por tipo).
 *
 * FASE-C: h1 próprio via <PageHeader>, modais feitos à mão trocados pelo
 * <Dialog> canônico, `confirm()` nativo → ConfirmDialog, setas/✕ viraram
 * ícones Lucide e "Workflow steps"/"gates" viraram "Etapas de aprovação".
 *
 * Estrutura:
 *   - Painel esquerdo: lista de tipos com toggle ativo
 *   - Painel direito: detail do tipo selecionado + workflow steps editor
 *   - Dialog inline pra criar/editar tipo
 *
 * Permissões granulares por campo (Bloco 5.6=C) NÃO são editadas aqui
 * nesta sub-fase MVP — seeds da migration 129 cobrem matriz inicial.
 * Editor de matriz vai numa rodada futura quando o caso de uso emergir.
 *
 * NÃO REVERTER:
 *   - 409 em duplicação de nome → toast amigável
 *   - Soft-delete via ativo=false (preserva histórico de projetos vinculados)
 *   - Workflow steps ordenados por `ordem` (não por criação)
 *   - perfilAprovador opcional — null = step sem aprovação específica
 */
import { useMemo, useState } from 'react';
import { CornerDownRight, X } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Select,
  SelectOption,
  Switch,
  useConfirmDialog,
} from '@/components/ui';
import {
  useProjetoTipos,
  useProjetoTipo,
  useCreateProjetoTipo,
  useUpdateProjetoTipo,
  useDeleteProjetoTipo,
  useCreateWorkflowStep,
  useDeleteWorkflowStep,
  type ProjetoTipo,
  type WorkflowStep,
} from '@/lib/projetos-store';

export function ProjetoTiposPage() {
  const tiposQuery = useProjetoTipos();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const tipos = useMemo(() => tiposQuery.data?.items ?? [], [tiposQuery.data]);

  // M1: agrupa subtipos sob o tipo pai (lista em 2 níveis).
  const { topLevel, subByParent } = useMemo(() => {
    const subs = new Map<string, ProjetoTipo[]>();
    for (const t of tipos) {
      if (t.parentTipoId) {
        const arr = subs.get(t.parentTipoId) ?? [];
        arr.push(t);
        subs.set(t.parentTipoId, arr);
      }
    }
    const known = new Set(tipos.map((t) => t.id));
    // Tipos de topo: parent null OU parent inexistente (órfão vira topo).
    const top = tipos.filter((t) => !t.parentTipoId || !known.has(t.parentTipoId));
    return { topLevel: top, subByParent: subs };
  }, [tipos]);

  function renderTipoButton(t: ProjetoTipo, isSub: boolean) {
    return (
      <button
        type="button"
        onClick={() => setSelectedId(t.id)}
        data-testid={`tipo-list-${t.id}`}
        className={`w-full text-left px-2 py-2 rounded text-sm flex items-center justify-between gap-2 ${
          isSub ? 'pl-5' : ''
        } ${
          selectedId === t.id
            ? 'bg-red-soft dark:bg-red-soft-dark text-ink-1 dark:text-ink-dark-1'
            : 'hover:bg-paper-warm dark:hover:bg-paper-dark-warm text-ink-2 dark:text-ink-dark-2'
        }`}
      >
        <span className="min-w-0 truncate font-medium">
          {/* FASE-C: a seta ↳ do subtipo virou ícone Lucide. */}
          {isSub && (
            <CornerDownRight
              className="mr-1 inline-block w-3.5 h-3.5 align-[-2px] text-ink-3 dark:text-ink-dark-3"
              aria-hidden="true"
            />
          )}
          {t.nome}
        </span>
        {!t.ativo && <Badge variant="neutral">Inativo</Badge>}
      </button>
    );
  }

  return (
    <div className="min-w-0">
      {/* FASE-C (copy): "Workflow configurável por tipo (Bloco 5.3=C)" era
          jargão de rodada vazando pra UI. O padding de página saiu — o
          AdminLayout já aplica px-4 md:px-8 (padding duplo comia o 375px). */}
      <PageHeader
        crumb="Configurações · Operação"
        title="Tipos de projeto"
        subtitle="Cada tipo define as etapas padrão que o cronograma recebe quando um projeto desse tipo é iniciado."
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="btn-create-tipo">
            + Novo tipo
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Painel esquerdo — lista */}
        <Card className="min-w-0 p-3 lg:col-span-1">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-ink-1 dark:text-ink-dark-1">Tipos</h2>
          </div>

          {tiposQuery.isLoading ? (
            <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
          ) : tipos.length === 0 ? (
            <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">
              Nenhum tipo ainda. Crie o primeiro no botão acima.
            </p>
          ) : (
            <ul className="space-y-1">
              {topLevel.map((t) => (
                <li key={t.id}>
                  {renderTipoButton(t, false)}
                  {(subByParent.get(t.id) ?? []).length > 0 && (
                    <ul className="space-y-1 mt-1">
                      {(subByParent.get(t.id) ?? []).map((sub) => (
                        <li key={sub.id}>{renderTipoButton(sub, true)}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Painel direito — detail */}
        <div className="min-w-0 lg:col-span-2">
          {selectedId ? (
            <TipoDetail tipoId={selectedId} onDeleted={() => setSelectedId(null)} />
          ) : (
            <EmptyState
              title="Escolha um tipo"
              description="Clique num tipo da lista pra editar o nome, montar as etapas padrão e definir as aprovações."
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateTipoDialog onClose={() => setShowCreate(false)} onCreated={(id) => setSelectedId(id)} />
      )}
    </div>
  );
}

// ─── TipoDetail ──────────────────────────────────────────────────────

function TipoDetail({ tipoId, onDeleted }: { tipoId: string; onDeleted: () => void }) {
  const tipoQuery = useProjetoTipo(tipoId);
  const tiposQuery = useProjetoTipos();
  const updateTipo = useUpdateProjetoTipo(tipoId);
  const deleteTipo = useDeleteProjetoTipo();
  // FASE-C: `confirm()` nativo → ConfirmDialog canônico.
  const confirmar = useConfirmDialog();
  const [editing, setEditing] = useState(false);

  const tipo = tipoQuery.data;
  const parentNome = tipo?.parentTipoId
    ? (tiposQuery.data?.items ?? []).find((t) => t.id === tipo.parentTipoId)?.nome ?? null
    : null;

  if (tipoQuery.isLoading) {
    return <Card className="p-4 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</Card>;
  }
  if (!tipo) {
    return <Card className="p-4 text-sm text-red dark:text-red-glow">Tipo não encontrado.</Card>;
  }

  async function handleToggleAtivo(novoAtivo: boolean) {
    await updateTipo.mutateAsync({ ativo: novoAtivo });
  }
  async function handleDelete() {
    const ok = await confirmar({
      title: 'Excluir tipo de projeto',
      message: `Excluir o tipo "${tipo!.nome}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    await deleteTipo.mutateAsync(tipoId);
    onDeleted();
  }

  return (
    <div className="min-w-0 space-y-3">
      <Card className="min-w-0 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-ink-1 dark:text-ink-dark-1 break-words">
              {tipo.nome}
            </h2>
            {parentNome && (
              <p className="text-xs text-ink-2 dark:text-ink-dark-2">
                Subtipo de <span className="font-medium">{parentNome}</span>
              </p>
            )}
            <p className="flex flex-wrap items-center gap-1 text-xs text-ink-3 dark:text-ink-dark-3">
              <span className="font-mono break-all">{tipo.id}</span>
              <HelpTip texto="Identificador interno deste tipo. Serve pra suporte técnico — você não precisa dele no dia a dia." />
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Switch
              checked={tipo.ativo}
              onChange={(e) => handleToggleAtivo(e.target.checked)}
              label="Ativo"
              data-testid="switch-ativo"
            />
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="btn-edit">
              Editar
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="btn-delete">
              Excluir
            </Button>
          </div>
        </div>
        {tipo.descricao && (
          <p className="text-sm text-ink-2 dark:text-ink-dark-2">{tipo.descricao}</p>
        )}
      </Card>

      {editing && (
        <EditTipoDialog
          tipo={tipo}
          onClose={() => setEditing(false)}
        />
      )}

      <WorkflowStepsEditor tipoId={tipoId} steps={tipo.workflowSteps ?? []} />
    </div>
  );
}

function mensagemErroTipo(msg: string): string {
  if (msg.includes('409') || msg.includes('duplic')) return 'Já existe um tipo com esse nome.';
  if (msg.includes('parent-self')) return 'Um tipo não pode ser pai de si mesmo.';
  if (msg.includes('parent-not-found')) return 'Tipo pai não encontrado.';
  if (msg.includes('parent-nao-pode-ser-subtipo'))
    return 'O tipo pai precisa ser um tipo de topo (não um subtipo).';
  if (msg.includes('tipo-tem-subtipos'))
    return 'Este tipo já tem subtipos, então não pode virar subtipo de outro.';
  return msg;
}

// ─── CreateTipoDialog ────────────────────────────────────────────────

function CreateTipoDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const createTipo = useCreateProjetoTipo();
  const tiposQuery = useProjetoTipos();
  const topLevel = (tiposQuery.data?.items ?? []).filter((t) => t.parentTipoId === null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [parentTipoId, setParentTipoId] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    try {
      const created = await createTipo.mutateAsync({
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        parentTipoId: parentTipoId || null,
      });
      onCreated(created.id);
      onClose();
    } catch (err) {
      setError(mensagemErroTipo(err instanceof Error ? err.message : 'Erro'));
    }
  }

  // FASE-C: modal feito à mão (`fixed inset-0 bg-black/40`, cor crua e sem
  // ESC/foco) trocado pelo <Dialog> canônico. O data-testid foi preservado.
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <div data-testid="create-tipo-dialog">
        <DialogHeader>
          <DialogTitle>Novo tipo de projeto</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tipo-nome" required>
                Nome
              </Label>
              <Input
                id="tipo-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: CFTV — Instalação completa"
                data-testid="input-tipo-nome"
              />
            </div>
            <div>
              <Label htmlFor="tipo-parent">Tipo pai (opcional)</Label>
              <Select
                id="tipo-parent"
                value={parentTipoId}
                onChange={(e) => setParentTipoId(e.target.value)}
                data-testid="select-tipo-parent"
              >
                <SelectOption value="">— Nenhum (tipo de topo) —</SelectOption>
                {topLevel.map((t) => (
                  <SelectOption key={t.id} value={t.id}>
                    {t.nome}
                  </SelectOption>
                ))}
              </Select>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                Escolha um pai para criar um subtipo (ex.: CFTV → CFTV Perimetral).
              </p>
            </div>
            <div>
              <Label htmlFor="tipo-descricao">Descrição</Label>
              <textarea
                id="tipo-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={3}
                className="w-full min-w-0 px-3 py-2 rounded border border-line-soft dark:border-line-soft-dark bg-paper-warm dark:bg-paper-dark-warm text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 text-sm focus:outline-none focus:border-red"
                placeholder="O que esse tipo cobre?"
                data-testid="input-tipo-descricao"
              />
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!nome.trim() || createTipo.isPending}
            data-testid="btn-submit-tipo"
          >
            {createTipo.isPending ? 'Criando…' : 'Criar'}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

// ─── EditTipoDialog ──────────────────────────────────────────────────

function EditTipoDialog({ tipo, onClose }: { tipo: ProjetoTipo; onClose: () => void }) {
  const updateTipo = useUpdateProjetoTipo(tipo.id);
  const tiposQuery = useProjetoTipos();
  // Pais candidatos: tipos de topo, exceto o próprio tipo (não pode ser pai de si).
  const topLevel = (tiposQuery.data?.items ?? []).filter(
    (t) => t.parentTipoId === null && t.id !== tipo.id,
  );
  const [nome, setNome] = useState(tipo.nome);
  const [descricao, setDescricao] = useState(tipo.descricao ?? '');
  const [parentTipoId, setParentTipoId] = useState(tipo.parentTipoId ?? '');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    try {
      await updateTipo.mutateAsync({
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        parentTipoId: parentTipoId || null,
      });
      onClose();
    } catch (err) {
      setError(mensagemErroTipo(err instanceof Error ? err.message : 'Erro'));
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <div data-testid="edit-tipo-dialog">
        <DialogHeader>
          <DialogTitle>Editar tipo de projeto</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-3">
            <div>
              <Label htmlFor="edit-nome" required>
                Nome
              </Label>
              <Input id="edit-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="edit-parent">Tipo pai (opcional)</Label>
              <Select
                id="edit-parent"
                value={parentTipoId}
                onChange={(e) => setParentTipoId(e.target.value)}
                data-testid="select-edit-parent"
              >
                <SelectOption value="">— Nenhum (tipo de topo) —</SelectOption>
                {topLevel.map((t) => (
                  <SelectOption key={t.id} value={t.id}>
                    {t.nome}
                  </SelectOption>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-descricao">Descrição</Label>
              <textarea
                id="edit-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={3}
                className="w-full min-w-0 px-3 py-2 rounded border border-line-soft dark:border-line-soft-dark bg-paper-warm dark:bg-paper-dark-warm text-ink-1 dark:text-ink-dark-1 text-sm"
              />
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!nome.trim() || updateTipo.isPending}>
            {updateTipo.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

// ─── WorkflowStepsEditor ─────────────────────────────────────────────

function WorkflowStepsEditor({
  tipoId,
  steps,
}: {
  tipoId: string;
  steps: WorkflowStep[];
}) {
  const createStep = useCreateWorkflowStep(tipoId);
  const deleteStep = useDeleteWorkflowStep(tipoId);
  const [showForm, setShowForm] = useState(false);
  const [nome, setNome] = useState('');
  const [requerAprovacao, setRequerAprovacao] = useState(false);
  const [perfilAprovador, setPerfilAprovador] = useState('');

  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.ordem - b.ordem),
    [steps],
  );
  const nextOrdem = sortedSteps.length > 0 ? Math.max(...sortedSteps.map((s) => s.ordem)) + 1 : 1;

  async function handleAdd() {
    if (!nome.trim()) return;
    await createStep.mutateAsync({
      ordem: nextOrdem,
      nome: nome.trim(),
      requerAprovacao,
      perfilAprovador: requerAprovacao && perfilAprovador ? perfilAprovador : null,
    });
    setNome('');
    setRequerAprovacao(false);
    setPerfilAprovador('');
    setShowForm(false);
  }

  // FASE-C (copy): "Workflow steps" / "Gates de aprovação" / "+ Step" eram
  // jargão de dev na cara do admin. Viraram "Etapas de aprovação".
  return (
    <Card className="min-w-0 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-ink-1 dark:text-ink-dark-1">
            Etapas de aprovação ({sortedSteps.length})
          </h3>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            Pontos em que o projeto precisa do aval de alguém antes de seguir. A ordem da
            lista é a ordem em que elas acontecem.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowForm((s) => !s)} data-testid="btn-add-step">
          {showForm ? 'Cancelar' : '+ Etapa de aprovação'}
        </Button>
      </div>

      {showForm && (
        <Card
          className="min-w-0 p-3 mb-3 bg-paper-warm dark:bg-paper-dark-warm"
          data-testid="form-add-step"
        >
          <div className="space-y-2">
            <div>
              <Label>Nome da etapa</Label>
              <Input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Aprovação comercial"
                data-testid="input-step-nome"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={requerAprovacao}
                onChange={(e) => setRequerAprovacao(e.target.checked)}
                data-testid="checkbox-aprovacao"
              />
              <span className="text-ink-1 dark:text-ink-dark-1">
                O projeto trava aqui até alguém aprovar
              </span>
            </label>
            {requerAprovacao && (
              <div>
                <Label>Quem pode aprovar</Label>
                <Select
                  value={perfilAprovador}
                  onChange={(e) => setPerfilAprovador(e.target.value)}
                  data-testid="select-perfil-aprovador"
                >
                  <SelectOption value="">— qualquer perfil —</SelectOption>
                  <SelectOption value="admin">Admin</SelectOption>
                  <SelectOption value="administrativo">Administrativo</SelectOption>
                  <SelectOption value="comercial">Comercial</SelectOption>
                  <SelectOption value="tecnico">Técnico</SelectOption>
                </Select>
              </div>
            )}
            <Button
              onClick={handleAdd}
              disabled={!nome.trim() || createStep.isPending}
              data-testid="btn-submit-step"
            >
              {createStep.isPending ? 'Criando…' : 'Adicionar etapa'}
            </Button>
          </div>
        </Card>
      )}

      {sortedSteps.length === 0 ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic text-center py-4">
          Nenhuma etapa de aprovação. Projetos deste tipo seguem direto, sem travas.
        </p>
      ) : (
        <ol className="space-y-2">
          {sortedSteps.map((s) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-2 p-2 rounded border border-line-soft dark:border-line-soft-dark"
              data-testid={`step-${s.id}`}
            >
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-ink-3 dark:text-ink-dark-3">
                  #{s.ordem}
                </span>
                <span className="min-w-0 font-medium text-ink-1 dark:text-ink-dark-1 truncate">
                  {s.nome}
                </span>
                {s.requerAprovacao && (
                  <Badge variant="warning">
                    {s.perfilAprovador
                      ? `Aprova: ${s.perfilAprovador}`
                      : 'Precisa de aprovação'}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteStep.mutate(s.id)}
                aria-label={`Remover a etapa ${s.nome}`}
                data-testid={`step-delete-${s.id}`}
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
